import React, { useState, useEffect } from 'react'
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Modal
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSave, faArrowLeft, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import { useParams, useHistory } from 'react-router-dom'
import { connect } from 'react-redux'
import { withNamespaces } from 'react-i18next'
import {
  getKitchenDisplayDetails,
  updateKitchenDisplay,
  getCategoriesApi,
  getKitchenDisplayList
} from '../Controller/KitchenDisplayController'
import Utility from '../../../../GlobalModules/Utility/Utility'
import '../Assets/css/kitchen-display.scss'

const EditKitchenDisplay = (props) => {
  const { lng, slack } = useParams()
  const history = useHistory()

  const [formData, setFormData] = useState({
    kitchen_display_code: '',
    label: '',
    status: '',
    orange_timer: '',
    red_timer: '',
    categories: [],
    is_default: 0,
    printer_id: ''
  })

  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [availableCategories, setAvailableCategories] = useState([])
  const [categoriesLoading, setCategoriesLoading] = useState(true)
  const [allKitchenDisplays, setAllKitchenDisplays] = useState([])
  const [showConflictModal, setShowConflictModal] = useState(false)
  const [conflictList, setConflictList] = useState([])

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setInitialLoading(true)
        setCategoriesLoading(true)
        const storeId = localStorage.getItem('store_id')

        const [catRes, detailsRes, listRes] = await Promise.allSettled([
          getCategoriesApi(),
          getKitchenDisplayDetails(slack),
          getKitchenDisplayList({ store_id: storeId, limit: 100 })
        ])

        let fetchedCategories = (catRes.status === 'fulfilled' && catRes.value?.success) ? (catRes.value.data || []) : []

        if (listRes.status === 'fulfilled' && listRes.value?.success) {
          setAllKitchenDisplays(listRes.value.data || [])
        }

        if (detailsRes.status === 'fulfilled' && detailsRes.value?.success) {
          const data = detailsRes.value.data
          if (Array.isArray(data.categories)) {
            data.categories.forEach(cat => {
              const catId = typeof cat === 'object' && cat !== null ? Number(cat.id) : Number(cat)
              if (!isNaN(catId) && !fetchedCategories.some(c => Number(c.id) === catId)) {
                fetchedCategories.push({
                  id: catId,
                  name: (typeof cat === 'object' && cat?.name) ? cat.name : `Category #${catId}`
                })
              }
            })
          }
          setAvailableCategories(fetchedCategories)
          setFormData({
            kitchen_display_code: data.kitchen_display_code || '',
            label: data.label || '',
            status: data.status !== undefined && data.status !== null ? String(data.status) : '',
            orange_timer: data.orange_timer !== undefined && data.orange_timer !== null ? String(data.orange_timer) : '',
            red_timer: data.red_timer !== undefined && data.red_timer !== null ? String(data.red_timer) : '',
            is_default: data.is_default == 1 || data.is_default === true ? 1 : 0,
            printer_id: data.printer_id ? String(data.printer_id) : '',
            categories: Array.isArray(data.categories)
              ? data.categories
                  .map((category) => {
                    if (typeof category === 'object' && category !== null && category.id !== undefined) {
                      return Number(category.id)
                    }
                    return Number(category)
                  })
                  .filter((id) => !isNaN(id))
              : []
          })
        } else {
          setAvailableCategories(fetchedCategories)
          setErrors({
            submit: 'Failed to load Kitchen Display details'
          })
        }
      } catch (err) {
        console.error('Error loading kitchen display details:', err)
        setErrors({
          submit: 'An error occurred while loading Kitchen Display details'
        })
      } finally {
        setInitialLoading(false)
        setCategoriesLoading(false)
      }
    }

    if (slack) {
      loadInitialData()
    }
  }, [slack])

  const validateForm = () => {
    const newErrors = {}

    // Kitchen Display Code validation
    if (!formData.kitchen_display_code.trim()) {
      newErrors.kitchen_display_code = 'Kitchen Display Code is required'
    } else {
      const code = formData.kitchen_display_code.trim()

      // Length validation
      if (code.length < 3) {
        newErrors.kitchen_display_code =
          'Kitchen Display Code must be at least 3 characters long'
      } else if (code.length > 50) {
        newErrors.kitchen_display_code =
          'Kitchen Display Code must not exceed 50 characters'
      }

      // Character validation - only alphanumeric, hyphens, and underscores allowed
      else if (!/^[a-zA-Z0-9_-]+$/.test(code)) {
        newErrors.kitchen_display_code =
          'Kitchen Display Code can only contain letters, numbers, hyphens, and underscores'
      }

      // Format validation - must start with a letter or number
      else if (!/^[a-zA-Z0-9]/.test(code)) {
        newErrors.kitchen_display_code =
          'Kitchen Display Code must start with a letter or number'
      }

      // Format validation - must end with a letter or number
      else if (!/[a-zA-Z0-9]$/.test(code)) {
        newErrors.kitchen_display_code =
          'Kitchen Display Code must end with a letter or number'
      }
    }

    // Name validation
    if (!formData.label.trim()) {
      newErrors.label = 'Name is required'
    }

    // Status validation
    if (!formData.status) {
      newErrors.status = 'Status is required'
    }

    // Orange Timer validation
    if (!formData.orange_timer) {
      newErrors.orange_timer = 'Please enter value in minutes'
    } else if (
      isNaN(formData.orange_timer) ||
      parseFloat(formData.orange_timer) <= 0
    ) {
      newErrors.orange_timer = 'Please enter a valid number in minutes'
    }

    // Red Timer validation
    if (!formData.red_timer) {
      newErrors.red_timer = 'Please enter value in minutes'
    } else if (
      isNaN(formData.red_timer) ||
      parseFloat(formData.red_timer) <= 0
    ) {
      newErrors.red_timer = 'Please enter a valid number in minutes'
    }

    // Validate that Red Timer is greater than Orange Timer
    if (formData.orange_timer && formData.red_timer) {
      const orangeTimer = parseFloat(formData.orange_timer)
      const redTimer = parseFloat(formData.red_timer)
      if (redTimer <= orangeTimer) {
        newErrors.red_timer = 'Red Timer must be greater than Orange Timer'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateField = (name, value) => {
    if (name === 'kitchen_display_code') {
      if (!value.trim()) {
        return 'Kitchen Display Code is required'
      } else {
        const code = value.trim()

        // Length validation
        if (code.length < 3) {
          return 'Kitchen Display Code must be at least 3 characters long'
        } else if (code.length > 50) {
          return 'Kitchen Display Code must not exceed 50 characters'
        }

        // Character validation - only alphanumeric, hyphens, and underscores allowed
        else if (!/^[a-zA-Z0-9_-]+$/.test(code)) {
          return 'Kitchen Display Code can only contain letters, numbers, hyphens, and underscores'
        }

        // Format validation - must start with a letter or number
        else if (!/^[a-zA-Z0-9]/.test(code)) {
          return 'Kitchen Display Code must start with a letter or number'
        }

        // Format validation - must end with a letter or number
        else if (!/[a-zA-Z0-9]$/.test(code)) {
          return 'Kitchen Display Code must end with a letter or number'
        }
      }
    }
    return ''
  }

  const clearError = (field) => {
    setErrors((prev) => {
      const newErrors = { ...prev }
      delete newErrors[field]
      return newErrors
    })
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    clearError(name)
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }))

    // Real-time validation
    const fieldError = validateField(name, value)
    setErrors((prev) => ({
      ...prev,
      [name]: fieldError
    }))
  }

  const handleCategoryChange = (categoryId) => {
    const numericId = Number(categoryId)
    setFormData((prev) => {
      const currentCategories = Array.isArray(prev.categories) ? prev.categories : []
      const isChecked = currentCategories.includes(numericId)
      const updatedCategories = isChecked
        ? currentCategories.filter((id) => id !== numericId)
        : [...currentCategories, numericId]
      return {
        ...prev,
        categories: updatedCategories
      }
    })
  }

  const executeSubmit = async () => {
    setLoading(true)

    try {
      const payload = {
        kitchen_display_code: formData.kitchen_display_code.trim(),
        label: formData.label.trim(),
        status: parseInt(formData.status),
        orange_timer: parseFloat(formData.orange_timer),
        red_timer: parseFloat(formData.red_timer),
        is_default: formData.is_default ? 1 : 0,
        categories: Array.isArray(formData.categories)
          ? formData.categories
          : []
      }

      console.log('Sending payload:', payload) // Debug log

      const response = await updateKitchenDisplay(slack, payload)

      if (response.success) {
        Utility.toastNotifications(
          'Kitchen Display updated successfully!',
          'Success',
          'success'
        )
        setTimeout(() => {
          history.push(`/${lng}/settings/kitchen-displays`)
        }, 1500)
      } else {
        Utility.toastNotifications(
          response.message || 'Failed to update Kitchen Display',
          'Error',
          'error'
        )
      }
    } catch (err) {
      Utility.toastNotifications(
        'An error occurred while updating Kitchen Display',
        'Error',
        'error'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    // Check for categories assigned to other kitchen displays
    const selectedCategoryIds = Array.isArray(formData.categories) ? formData.categories.map(Number) : []
    const conflicts = selectedCategoryIds
      .map((catId) => {
        const otherKd = (allKitchenDisplays || []).find(
          (kd) =>
            kd.slack !== slack &&
            Array.isArray(kd.categories) &&
            kd.categories.map(Number).includes(Number(catId))
        )
        if (otherKd) {
          const cat = (availableCategories || []).find((c) => Number(c.id) === Number(catId))
          return {
            categoryId: catId,
            categoryName: cat ? cat.name : `Category #${catId}`,
            stationName: otherKd.label || otherKd.kitchen_display_code
          }
        }
        return null
      })
      .filter(Boolean)

    if (conflicts.length > 0) {
      setConflictList(conflicts)
      setShowConflictModal(true)
      return
    }

    await executeSubmit()
  }

  if (initialLoading) {
    return (
      <Container fluid className='p-0'>
        <div
          className='d-flex justify-content-center align-items-center'
          style={{ minHeight: '400px' }}
        >
          <Spinner animation='border' variant='primary' />
          <span className='ms-2'>Loading Kitchen Display details...</span>
        </div>
      </Container>
    )
  }

  return (
    <Container fluid className='p-0'>
      <div className='d-flex justify-content-between align-items-center mb-4'>
        <div>
          <h4 className='mb-1'>Edit Kitchen Display</h4>
          <p className='text-muted mb-0'>
            Update kitchen display station settings and category routing
          </p>
        </div>
        <Button
          variant='outline-secondary'
          onClick={() => history.push(`/${lng}/settings/kitchen-displays`)}
        >
          <FontAwesomeIcon icon={faArrowLeft} className='me-2' />
          Back to List
        </Button>
      </div>

      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className='mb-3'>
                  <Form.Label>
                    Kitchen Display Code <span className='text-danger'>*</span>
                  </Form.Label>
                  <Form.Control
                    type='text'
                    name='kitchen_display_code'
                    value={formData.kitchen_display_code}
                    onChange={handleInputChange}
                    placeholder='Enter kitchen display code'
                    isInvalid={!!errors.kitchen_display_code}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {errors.kitchen_display_code}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className='mb-3'>
                  <Form.Label>
                    Name/Label <span className='text-danger'>*</span>
                  </Form.Label>
                  <Form.Control
                    type='text'
                    name='label'
                    value={formData.label}
                    onChange={handleInputChange}
                    placeholder='Enter name/label'
                    isInvalid={!!errors.label}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {errors.label}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className='mb-3'>
                  <Form.Label>
                    Status <span className='text-danger'>*</span>
                  </Form.Label>
                  <Form.Select
                    name='status'
                    value={formData.status}
                    onChange={handleInputChange}
                    isInvalid={!!errors.status}
                  >
                    <option value=''>Choose status..</option>
                    <option value='1'>Active</option>
                    <option value='0'>Inactive</option>
                  </Form.Select>
                  <Form.Control.Feedback type='invalid'>
                    {errors.status}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className='mb-3'>
                  <Form.Label>Orange Timer (Mins)</Form.Label>
                  <Form.Control
                    type='text'
                    name='orange_timer'
                    value={formData.orange_timer}
                    onChange={handleInputChange}
                    placeholder='Please enter value in minutes'
                    isInvalid={!!errors.orange_timer}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {errors.orange_timer}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className='mb-3'>
                  <Form.Label>Red Timer (Mins)</Form.Label>
                  <Form.Control
                    type='text'
                    name='red_timer'
                    value={formData.red_timer}
                    onChange={handleInputChange}
                    placeholder='Please enter value in minutes'
                    isInvalid={!!errors.red_timer}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {errors.red_timer}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className='mb-4 position-relative'>
                  <Form.Label>Default Display Settings</Form.Label>
                  <div className='mt-2'>
                    <Form.Check
                      type='switch'
                      id='edit_is_default_switch'
                      name='is_default'
                      label='Mark as Default Kitchen Display'
                      checked={Boolean(formData.is_default)}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          is_default: e.target.checked ? 1 : 0
                        }))
                      }
                    />
                    <Form.Text className='text-muted'>
                      New categories serving POS billing will be automatically assigned to the default display.
                    </Form.Text>
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className='mb-3'>
                  <Form.Label>Categories</Form.Label>
                  <div className='d-flex flex-wrap gap-3'>
                    {categoriesLoading ? (
                      <div className='text-center text-muted w-100'>
                        <small>Loading categories...</small>
                      </div>
                    ) : availableCategories.length > 0 ? (
                      availableCategories.map((category) => {
                        const currentAssignedKd = (allKitchenDisplays || []).find(
                          (kd) =>
                            kd.slack !== slack &&
                            Array.isArray(kd.categories) &&
                            kd.categories.map(Number).includes(Number(category.id))
                        )
                        return (
                          <Form.Check
                            key={category.id}
                            type='checkbox'
                            id={`category-${category.id}`}
                            label={
                              <span>
                                {category.name}
                                {currentAssignedKd && (
                                  <span
                                    className='badge bg-light text-muted border ms-2'
                                    style={{ fontSize: '0.75rem', fontWeight: 'normal' }}
                                  >
                                    in {currentAssignedKd.label || currentAssignedKd.kitchen_display_code}
                                  </span>
                                )}
                              </span>
                            }
                            checked={
                              Array.isArray(formData.categories) &&
                              formData.categories.includes(Number(category.id))
                            }
                            onChange={() => handleCategoryChange(category.id)}
                            className='me-3 mb-2'
                          />
                        )
                      })
                    ) : (
                      <div className='text-center text-muted w-100'>
                        <small>No categories available</small>
                      </div>
                    )}
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <div className='d-flex justify-content-end gap-2'>
              <Button
                variant='outline-secondary'
                onClick={() =>
                  history.push(`/${lng}/settings/kitchen-displays`)
                }
                disabled={loading}
              >
                Cancel
              </Button>
              <Button variant='primary' type='submit' disabled={loading}>
                {loading ? (
                  <>
                    <Spinner
                      as='span'
                      animation='border'
                      size='sm'
                      role='status'
                      aria-hidden='true'
                      className='me-2'
                    />
                    Updating...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className='me-2' />
                    Save
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Category Reassignment Confirmation Modal */}
      <Modal
        show={showConflictModal}
        onHide={() => setShowConflictModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className='text-warning d-flex align-items-center gap-2'>
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>Category Reassignment</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='mb-2'>
            The following categories are already selected in other kitchen displays:
          </p>
          <div className='p-3 bg-light rounded border mb-3'>
            <ul className='mb-0 ps-3'>
              {conflictList.map((item, idx) => (
                <li key={idx} className='mb-1'>
                  <strong>{item.categoryName}</strong> is currently assigned to <strong>[{item.stationName}]</strong>
                </li>
              ))}
            </ul>
          </div>
          <p className='text-muted small mb-0'>
            A category can only be assigned to one kitchen station at a time. Assigning them here will remove them from their previous kitchen stations and give preference to this kitchen display.
            <br />
            <br />
            <strong>Would you like to assign them here?</strong>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='outline-secondary'
            onClick={() => setShowConflictModal(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={() => {
              setShowConflictModal(false)
              executeSubmit()
            }}
            disabled={loading}
          >
            Yes, Reassign & Save
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

const mapStateToProps = (globalState) => {
  return {
    userCredentials: globalState.LoginReducer.userCredentials,
    token: globalState.LoginReducer.token,
    userEntryType: globalState.LoginReducer.userEntryType
  }
}

export default connect(mapStateToProps)(withNamespaces()(EditKitchenDisplay))
